import React, { useMemo, useState } from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';
import { buildHierarchyTree } from '../utils/hierarchyTree';

/**
 * Tree-indented, collapsible picker for "this node, optionally plus everything
 * beneath it" -- the Applicability Group membership shape. Kept separate from
 * LookupMultiSelect because that component's selection is a plain id array;
 * this one needs a second, per-row checkbox that a plain array can't carry, so
 * `selection` is `{ [nodeId]: includeDescendants }`.
 */
function HierarchyNodeTreeMultiSelect({
  nodes, selection, onChange, emptyMessage, defaultIncludeDescendants = false,
}) {
  const [query, setQuery] = useState('');
  const tree = useMemo(() => buildHierarchyTree(nodes), [nodes]);

  const trimmedQuery = query.trim().toLowerCase();
  const searching = trimmedQuery.length > 0;
  const visible = searching
    ? nodes.filter((n) => String(n.display_path || n.name).toLowerCase().includes(trimmedQuery))
    : tree;

  const toggleNode = (id) => {
    const next = { ...selection };
    if (id in next) {
      delete next[id];
    } else {
      // Applicability Group membership (the original caller here) defaults a
      // freshly-picked node to false -- an admin building "these specific
      // locations" opts into the subtree deliberately. A user's home scope
      // (issue #17) is the opposite instinct: picking a facility as your
      // default should include its departments without a second click, so
      // that caller passes defaultIncludeDescendants.
      next[id] = defaultIncludeDescendants;
    }
    onChange(next);
  };

  const toggleDescendants = (id) => {
    onChange({ ...selection, [id]: !selection[id] });
  };

  if (nodes.length === 0) {
    return <div style={{ fontSize: '13px', color: 'var(--muted)' }}>{emptyMessage}</div>;
  }

  const selectedNodes = nodes.filter((n) => n.id in selection);
  const summary = selectedNodes.length === 0
    ? 'No specific locations'
    : selectedNodes.length <= 2
      ? selectedNodes.map((n) => n.name).join(', ')
      : `${selectedNodes.length} locations selected`;

  return (
    <MultiSelectDropdown label={summary}>
      <input
        type="text"
        className="multiselect-search"
        placeholder="Filter locations..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <div className="multiselect-options">
        {visible.map((node) => {
          const selected = node.id in selection;
          return (
            <div key={node.id} className="multiselect-tree-row">
              <label
                className="multiselect-option"
                style={!searching ? { paddingLeft: `${10 + node.depth * 16}px` } : undefined}
              >
                <input type="checkbox" checked={selected} onChange={() => toggleNode(node.id)} />
                {searching ? (node.display_path || node.name) : node.name}
              </label>
              {selected && (
                <label className="multiselect-tree-suboption">
                  <input
                    type="checkbox"
                    checked={!!selection[node.id]}
                    onChange={() => toggleDescendants(node.id)}
                  />
                  ...and everything beneath it
                </label>
              )}
            </div>
          );
        })}
        {visible.length === 0 && (
          <div style={{ fontSize: '13px', color: 'var(--muted)', padding: '6px 8px' }}>No matches.</div>
        )}
      </div>
    </MultiSelectDropdown>
  );
}

export default HierarchyNodeTreeMultiSelect;
